
/**
 * Azure Blob Storage Service
 * Handles operations related to blob storage including uploads, downloads, 
 * and generating SAS tokens for secure access.
 */

import {
  BlobServiceClient,
  ContainerClient,
  BlockBlobClient
} from '@azure/storage-blob';
import apiClient from '../api/client';
import config from '../../utils/config';
import { debugLog } from '../../utils/debug';
import { AxiosProgressEvent } from 'axios';
import { getEnvVar } from '../../utils/env';

// Add Vite env type declarations
interface ImportMetaEnv {
  VITE_AZURE_STORAGE_ACCOUNT_NAME: string;
  VITE_AZURE_STORAGE_ACCOUNT_KEY: string;
  VITE_AZURE_STORAGE_CONNECTION_STRING: string;
  VITE_USE_API_PROXY: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// Interface for file upload progress tracking
export interface UploadProgressInfo {
  filename: string;
  bytesTransferred: number;
  totalBytes: number;
  percentComplete: number;
}

// Interface for upload options
export interface UploadOptions {
  containerName: string;
  blobName?: string;
  contentType?: string;
  metadata?: Record<string, string>;
  onProgress?: (progress: UploadProgressInfo) => void;
  clientId: string;
  workflowId: string;
  dataSource: string; // NEW
}

// Interface for download options
export interface DownloadOptions {
  containerName: string;
  blobName: string;
  onProgress?: (progress: number) => void;
}

// Interface for SAS token generation options
export interface SASOptions {
  containerName: string;
  blobName?: string;
  permissions: string; // 'r' for read, 'w' for write, 'c' for create, 'd' for delete
  expiryMinutes?: number;
}

// Interface for API response types
interface UploadResponse {
  blob_path: string;
}

/**
 * BlobStorageService class to interact with Azure Blob Storage
 * Provides both:
 * 1. Direct SDK access (when CORS/SAS is configured)
 * 2. API proxied access (more secure through server)
 */
export class BlobStorageService {
  private static instance: BlobStorageService;
  private blobServiceClient: BlobServiceClient | null = null;
  private accountName: string = '';
  private accountKey: string = '';
  private isConfigured: boolean = false;
  
  // Using mock mode only when necessary
  private useMockMode: boolean = false;
  
  // ALWAYS use API proxy for security and to avoid browser Buffer issues
  private useApiProxy: boolean = true;
  
  private connectionString: string = '';
  private containerName: string = 'client-data';
  private client: BlobServiceClient | null = null;
  
  constructor() {
    try {
      this.connectionString = getEnvVar('VITE_AZURE_STORAGE_CONNECTION_STRING', '');
      this.containerName = getEnvVar('VITE_BLOB_CONTAINER_NAME', 'client-data');
      
      // Force API proxy mode to avoid Buffer polyfill issues
      this.useApiProxy = true;
      
      debugLog('BlobStorage', 'Using API proxy mode for all storage operations');
      
      // Only try to initialize the client if we're not using API proxy mode
      // or if we need it for specific operations that can't be proxied
      if (this.connectionString && !this.useApiProxy) {
        try {
          this.client = BlobServiceClient.fromConnectionString(this.connectionString);
          debugLog('BlobStorage', 'BlobServiceClient initialized successfully');
        } catch (clientError: unknown) {
          debugLog('BlobStorage', 'Error initializing BlobServiceClient', clientError);
          this.client = null;
        }
      } else {
        // In API proxy mode, we don't need the client for most operations
        // so don't log an error - just note that we're using API proxy mode
        debugLog('BlobStorage', 'Storage service configured to use API proxy');
        this.client = null;
      }
      
      // Still try to check the env variables for future use
      this.initializeFromEnv();
    } catch (error: unknown) {
      // Catch any initialization errors and fall back to mock mode
      debugLog('BlobStorage', 'Error in constructor, falling back to mock mode', error);
      this.useMockMode = true;
      this.client = null;
    }
  }
  
  /**
   * Get singleton instance of BlobStorageService
   */
  public static getInstance(): BlobStorageService {
    if (!BlobStorageService.instance) {
      BlobStorageService.instance = new BlobStorageService();
    }
    return BlobStorageService.instance;
  }
  
  /**
   * Initialize blob service from environment variables
   */
  private initializeFromEnv(): void {
    try {
      // Always use API proxy mode
      this.useApiProxy = true;
      
      // We still check for credentials availability for logging purposes
      const hasAccountName = !!import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME;
      const hasConnectionString = !!import.meta.env.VITE_AZURE_STORAGE_CONNECTION_STRING;
      
      debugLog('BlobStorage', 'Storage service configured to use API proxy', {
        hasAccountName,
        hasConnectionString,
        useApiProxy: true
      });
    } catch (error) {
      debugLog('BlobStorage', 'Error in initializeFromEnv', error);
    }
  }
  
  /**
   * Configure blob service with account credentials
   */
  public configure(accountName: string, accountKey: string): void {
    try {
      this.accountName = accountName;
      this.accountKey = accountKey;
      
      // Create connection string
      const connectionString = `DefaultEndpointsProtocol=https;AccountName=${accountName};AccountKey=${accountKey};EndpointSuffix=core.windows.net`;
      
      this.blobServiceClient = BlobServiceClient.fromConnectionString(connectionString);
      
      this.isConfigured = true;
      this.useMockMode = false;
      debugLog('BlobStorage', 'Azure Blob Storage service configured successfully', {
        accountName
      });
    } catch (error) {
      debugLog('BlobStorage', 'Error configuring Azure Blob Storage', error);
      this.isConfigured = false;
      this.useMockMode = !this.useApiProxy;
    }
  }
  
  /**
   * Upload a file to blob storage
   */
  /**
   * Upload a file with data source information
   */
  public async uploadFileWithDataSource(
    file: File,
    dataSource: string,
    clientId: string,
    workflowId: string,
    onProgress?: (progress: UploadProgressInfo) => void
  ): Promise<string> {
    const options: UploadOptions = {
      containerName: this.containerName,
      clientId,
      workflowId,
      dataSource,
      onProgress
    };
    return this.uploadFileViaApi(file, options);
  }

  /**
   * Upload a file to blob storage via backend API proxy
   * @deprecated Use uploadFileDirectly() instead for better performance and scalability
   * This method uploads through the backend, which can cause memory issues with large files.
   * uploadFileDirectly() uses SAS tokens and uploads directly to Azure Blob Storage.
   */
  public async uploadFile(
    file: File,
    options: UploadOptions
  ): Promise<string> {
    try {
      // Always use API proxy if configured
      if (this.useApiProxy) {
        return this.uploadFileViaApi(file, options);
      }
      
      // Development - Use mock mode to avoid browser compatibility issues
      if (process.env.NODE_ENV === 'development' || this.useMockMode) {
        debugLog('BlobStorage', 'Using mock upload in development or mock mode');
        return this.mockUploadFile(file, options);
      }
      
      // Only use direct SDK in production with configured service
      if (!this.blobServiceClient || !this.isConfigured) {
        debugLog('BlobStorage', 'Blob client not configured, falling back to mock');
        return this.mockUploadFile(file, options);
      }
      
      // Continue with actual upload logic
      try {
        // Create container client
        const containerClient = this.blobServiceClient.getContainerClient(options.containerName);
        await this.ensureContainerExists(containerClient);
        
        // Generate a unique blob name if not provided
        const blobName = options.blobName || this.generateUniqueBlobName(file.name);
        
        // Create blob client
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();
        
        // Set up progress tracking
        const onProgress = options.onProgress;
        const reportProgress = onProgress
          ? (ev: { loadedBytes: number }) => {
              onProgress({
                filename: file.name,
                bytesTransferred: ev.loadedBytes,
                totalBytes: file.size,
                percentComplete: Math.round((ev.loadedBytes / file.size) * 100)
              });
            }
          : undefined;
        
        // Upload the file
        const response = await blockBlobClient.uploadData(await file.arrayBuffer(), {
          blobHTTPHeaders: {
            blobContentType: options.contentType || file.type
          },
          metadata: options.metadata,
          onProgress: reportProgress
        });
        
        debugLog('BlobStorage', 'Upload successful', response);
        
        // Return the path to the uploaded blob
        return `${options.containerName}/${blobName}`;
      } catch (uploadError) {
        debugLog('BlobStorage', 'Error in direct upload, falling back to mock', uploadError);
        return this.mockUploadFile(file, options);
      }
    } catch (error) {
      debugLog('BlobStorage', 'Error in uploadFile, falling back to mock', error);
      return this.mockUploadFile(file, options);
    }
  }
  
  /**
   * Upload a file via API proxy
   */
  private async uploadFileViaApi(
    file: File,
    options: UploadOptions
  ): Promise<string> {
    try {
      // Check required parameters
      if (!options.clientId || !options.workflowId) {
        throw new Error('Client ID and Workflow ID are required for file uploads');
      }
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Also include client_id in the form data for redundancy
      formData.append('client_id', options.clientId);
      formData.append('workflow_id', options.workflowId);
      formData.append('data_source', options.dataSource);
      
      // Set up progress tracking if supported
      const config: {
        headers: { 'Content-Type': string };
        onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
        params: { client_id: string; workflow_id: string; data_source: string; blob_name?: string; };
      } = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // Pass client_id and workflow_id as query parameters rather than in the form data
        params: {
          client_id: options.clientId,
          workflow_id: options.workflowId,
          data_source: options.dataSource
        }
      };
      
      // Add custom blob name as query parameter if provided
      if (options.blobName) {
        config.params.blob_name = options.blobName;
      }
      
      if (options.onProgress) {
        config.onUploadProgress = (progressEvent) => {
          if (!progressEvent.total) return;
          
          const percentComplete = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          
          options.onProgress!({
            filename: file.name,
            bytesTransferred: progressEvent.loaded,
            totalBytes: progressEvent.total,
            percentComplete
          });
        };
      }
      
      console.log('Uploading file to API with client_id:', options.clientId);
      console.log('Upload config:', { 
        containerName: options.containerName,
        clientId: options.clientId,
        workflowId: options.workflowId
      });
      
      // Send to API endpoint that handles the upload using POST method
      const response = await apiClient.post<UploadResponse>(
        `/api/v1/storage/upload/${options.containerName}`,
        formData,
        config
      );
      
      console.log('Upload response:', response.data);
      return response.data.blob_path;
    } catch (error) {
      console.error('Error uploading file via API:', error);
      debugLog('BlobStorage', 'Error uploading file via API', error);
      throw error;
    }
  }
  
  /**
   * Upload file directly to Azure Blob Storage using SAS token
   * Ideal for large files (100MB+) as it bypasses the backend
   */
  public async uploadFileDirectly(
    file: File,
    options: UploadOptions
  ): Promise<string> {
    try {
      // Check required parameters
      if (!options.clientId || !options.workflowId) {
        throw new Error('Client ID and Workflow ID are required for file uploads');
      }
      
      debugLog('BlobStorage', 'Starting direct upload for file', { 
        filename: file.name, 
        size: file.size,
        clientId: options.clientId,
        workflowId: options.workflowId
      });
      
      // Step 1: Get SAS URL from backend
      const sasResponse = await apiClient.get<{
        upload_url: string;
        blob_url: string;
        container_name: string;
        blob_name: string;
      }>('/api/v1/storage/upload-sas-url', {
        params: {
          container_name: options.containerName,
          client_id: options.clientId,
          workflow_id: options.workflowId,
          filename: options.blobName || file.name,
          data_source: options.dataSource
        }
      });
      
      const { upload_url, blob_url, container_name, blob_name } = sasResponse.data;
      
      debugLog('BlobStorage', 'Received SAS URL', { 
        blob_url,
        container_name,
        blob_name
      });
      
      // Step 2: Upload directly to Azure Blob Storage using BlockBlobClient from SDK
      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(upload_url);
      
      // Upload with progress tracking
      const uploadOptions: any = {
        blobHTTPHeaders: {
          blobContentType: file.type || 'application/octet-stream'
        },
        blockSize: 4 * 1024 * 1024, // 4MB blocks
        concurrency: 5, // Upload 5 blocks in parallel
        onProgress: (progress: { loadedBytes: number }) => {
          if (options.onProgress) {
            options.onProgress({
              filename: file.name,
              bytesTransferred: progress.loadedBytes,
              totalBytes: file.size,
              percentComplete: Math.round((progress.loadedBytes / file.size) * 100)
            });
          }
        }
      };
      
      debugLog('BlobStorage', 'Starting upload to Azure', { 
        blockSize: '4MB',
        concurrency: 5
      });
      
      await blockBlobClient.uploadData(file, uploadOptions);
      
      debugLog('BlobStorage', 'Upload completed successfully', { blob_url });
      
      // Return the path in the format expected by the rest of the application
      return `${container_name}/${blob_name}`;
    } catch (error) {
      console.error('Error uploading file directly:', error);
      debugLog('BlobStorage', 'Error uploading file directly', error);
      throw error;
    }
  }
  
  /**
   * Download a blob from storage
   */
  public async downloadBlob(
    options: DownloadOptions
  ): Promise<Blob> {
    if (this.useApiProxy) {
      return this.downloadBlobViaApi(options);
    }
    
    if (this.useMockMode) {
      return this.mockDownloadBlob(options);
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      // Get blob client
      const containerClient = this.blobServiceClient.getContainerClient(options.containerName);
      const blobClient = containerClient.getBlobClient(options.blobName);
      
      // Download the blob
      const downloadResponse = await blobClient.download();
      
      if (!downloadResponse.readableStreamBody) {
        throw new Error('No readable stream available');
      }
      
      // Convert stream to blob
      const blob = await this.streamToBlob(
        downloadResponse.readableStreamBody,
        downloadResponse.contentLength,
        options.onProgress
      );
      
      return blob;
    } catch (error) {
      debugLog('BlobStorage', 'Error downloading blob', error);
      throw error;
    }
  }
  
  /**
   * Download a blob via API proxy
   */
  private async downloadBlobViaApi(options: DownloadOptions): Promise<Blob> {
    try {
      // First get a download URL from the API
      const downloadUrlResponse = await apiClient.get<{ download_url: string }>(
        '/api/v1/storage/download',
        {
          params: {
            container: options.containerName,
            blob_path: options.blobName
          }
        }
      );
      
      // Use the download URL to fetch the blob
      const response = await fetch(downloadUrlResponse.data.download_url);
      
      if (!response.ok) {
        throw new Error(`Error downloading blob: ${response.statusText}`);
      }
      
      // Create a Reader from the response
      const reader = response.body?.getReader();
      
      if (!reader) {
        throw new Error('Unable to get response reader');
      }
      
      // Get total content length if available
      const contentLength = Number(response.headers.get('Content-Length')) || 0;
      
      // Read the blob in chunks and track progress
      const chunks: Uint8Array[] = [];
      let receivedLength = 0;
      
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }
        
        chunks.push(value);
        receivedLength += value.length;
        
        if (options.onProgress && contentLength > 0) {
          const percentComplete = Math.round((receivedLength / contentLength) * 100);
          options.onProgress(percentComplete);
        }
      }
      
      // Concatenate chunks into a single Uint8Array
      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }
      
      // Create a blob from the array
      const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
      return new Blob([chunksAll], { type: contentType });
    } catch (error) {
      debugLog('BlobStorage', 'Error downloading blob via API', error);
      throw error;
    }
  }
  
  /**
   * Get the content of a file as a string
   * @param filePath The full path to the file including container name, e.g. 'client-data/path/to/file.txt'
   * @returns The content of the file as a string
   */
  public async getFileContent(filePath: string): Promise<string> {
    try {
      // Split the path into container and blob name
      const parts = filePath.split('/');
      let containerName: string;
      let blobName: string;
      
      // Handle paths that might include or exclude the container name
      if (parts.length > 1) {
        // Check if the first part might be a container name
        if (['client-data', 'outputs', 'analysis'].includes(parts[0])) {
          containerName = parts[0];
          blobName = parts.slice(1).join('/');
        } else {
          // Use default container
          containerName = 'client-data';
          blobName = filePath;
        }
      } else {
        // If there's just one part, it must be just the blob name
        containerName = 'client-data';
        blobName = filePath;
      }
      
      console.log(`Getting file content from container: ${containerName}, blob: ${blobName}`);
      
      // Download the file as a blob
      const blob = await this.downloadBlob({
        containerName,
        blobName
      });
      
      // Convert blob to text
      const text = await blob.text();
      return text;
    } catch (error) {
      console.error('Error getting file content:', error);
      throw new Error(`Failed to get content of file ${filePath}: ${error}`);
    }
  }
  
  /**
   * Get a download URL for a blob
   */
  public async getDownloadUrl(
    blobPath: string,
    containerName: string = config.azure.containers.outputs
  ): Promise<string> {
    if (this.useApiProxy) {
      // Use API to get a download URL
      const response = await apiClient.get<{ download_url: string }>(
        '/api/v1/storage/download',
        {
          params: {
            container: containerName,
            blob_path: blobPath
          }
        }
      );
      
      return response.data.download_url;
    }
    
    if (this.useMockMode) {
      return this.mockGetPublicUrl(containerName, blobPath);
    }
    
    // Direct SDK approach using SAS token
    return this.getPublicUrl(containerName, blobPath);
  }
  
  /**
   * List blobs in a container
   */
  public async listBlobs(containerName: string, prefix?: string): Promise<string[]> {
    if (this.useApiProxy) {
      return this.listBlobsViaApi(containerName, prefix);
    }
    
    if (this.useMockMode) {
      return this.mockListBlobs(containerName, prefix);
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blobIterable = containerClient.listBlobsFlat({ prefix });
      
      const blobNames: string[] = [];
      for await (const blob of blobIterable) {
        blobNames.push(blob.name);
      }
      
      return blobNames;
    } catch (error) {
      debugLog('BlobStorage', 'Error listing blobs', error);
      throw error;
    }
  }
  
  /**
   * List blobs via API proxy
   */
  private async listBlobsViaApi(containerName: string, prefix?: string): Promise<string[]> {
    try {
      const response = await apiClient.get<{ blobs: string[] }>(
        '/api/v1/storage/list',
        {
          params: {
            container: containerName,
            prefix
          }
        }
      );
      
      return response.data.blobs;
    } catch (error) {
      debugLog('BlobStorage', 'Error listing blobs via API', error);
      throw error;
    }
  }
  
  /**
   * Generate a SAS token for secure access
   */
  public async generateSasToken(options: SASOptions): Promise<string> {
    if (this.useMockMode) {
      return this.mockGenerateSASToken();
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      // Set expiry time
      const expiryMinutes = options.expiryMinutes || 60;
      const expiryTime = new Date();
      expiryTime.setMinutes(expiryTime.getMinutes() + expiryMinutes);
      
      // Convert permission string to permission objects
      const permString = options.permissions || 'r';
      
      // Create connection string for SAS token generation
      const connectionString = `DefaultEndpointsProtocol=https;AccountName=${this.accountName};AccountKey=${this.accountKey};EndpointSuffix=core.windows.net`;
      const tempClient = BlobServiceClient.fromConnectionString(connectionString);
      
      // Generate SAS token based on whether it's for a blob or container
      if (options.blobName) {
        // For blob-level SAS
        const containerClient = tempClient.getContainerClient(options.containerName);
        const blobClient = containerClient.getBlobClient(options.blobName);
        const sasToken = await blobClient.generateSasUrl({
          permissions: permString as any, // Type assertion to bypass type checking
          expiresOn: expiryTime
        });
        
        // Extract the SAS token from the URL
        const sasTokenString = sasToken.split('?')[1];
        return sasTokenString;
      } else {
        // For container-level SAS
        const containerClient = tempClient.getContainerClient(options.containerName);
        const sasToken = await containerClient.generateSasUrl({
          permissions: permString as any, // Type assertion to bypass type checking
          expiresOn: expiryTime
        });
        
        // Extract the SAS token from the URL
        const sasTokenString = sasToken.split('?')[1];
        return sasTokenString;
      }
    } catch (error) {
      debugLog('BlobStorage', 'Error generating SAS token', error);
      throw error;
    }
  }
  
  /**
   * Get public URL for a blob with SAS token for read access
   */
  public getPublicUrl(containerName: string, blobName: string, expiryMinutes = 60): string {
    if (this.useMockMode) {
      return this.mockGetPublicUrl(containerName, blobName);
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      const sasToken = this.generateSasToken({
        containerName,
        blobName,
        permissions: 'r',
        expiryMinutes
      });
      
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlobClient(blobName);
      
      return `${blobClient.url}?${sasToken}`;
    } catch (error) {
      debugLog('BlobStorage', 'Error getting public URL', error);
      throw error;
    }
  }
  
  /**
   * Delete a blob
   */
  public async deleteBlob(containerName: string, blobName: string): Promise<void> {
    if (this.useApiProxy) {
      return this.deleteBlobViaApi(containerName, blobName);
    }
    
    if (this.useMockMode) {
      return this.mockDeleteBlob();
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlobClient(blobName);
      
      await blobClient.delete();
      debugLog('BlobStorage', `Blob ${blobName} deleted successfully`);
    } catch (error) {
      debugLog('BlobStorage', 'Error deleting blob', error);
      throw error;
    }
  }
  
  /**
   * Delete a blob via API proxy
   */
  private async deleteBlobViaApi(containerName: string, blobName: string): Promise<void> {
    try {
      await apiClient.delete('/api/v1/storage/delete', {
        params: {
          container: containerName,
          blob_path: blobName
        }
      });
      
      debugLog('BlobStorage', `Blob ${blobName} deleted successfully via API`);
    } catch (error) {
      debugLog('BlobStorage', 'Error deleting blob via API', error);
      throw error;
    }
  }
  
  /**
   * Check if a blob exists
   */
  public async blobExists(containerName: string, blobName: string): Promise<boolean> {
    if (this.useApiProxy) {
      return this.blobExistsViaApi(containerName, blobName);
    }
    
    if (this.useMockMode) {
      return true; // Always return true in mock mode
    }
    
    if (!this.isConfigured || !this.blobServiceClient) {
      throw new Error('Blob Storage service not configured');
    }
    
    try {
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlobClient(blobName);
      
      return await blobClient.exists();
    } catch (error) {
      debugLog('BlobStorage', 'Error checking if blob exists', error);
      throw error;
    }
  }
  
  /**
   * Check if a blob exists via API proxy
   */
  private async blobExistsViaApi(containerName: string, blobName: string): Promise<boolean> {
    try {
      // Use GET request with empty response type instead of HEAD to avoid browser issues
      const response = await apiClient.get('/api/v1/storage/exists', {
        params: {
          container: containerName,
          blob_path: blobName
        },
        // Don't expect any response body
        responseType: 'text'
      });
      
      return response.status === 200;
    } catch (error: unknown) {
      // Handle axios errors which have response property
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status: number } };
        if (axiosError.response && axiosError.response.status === 404) {
          return false;
        }
      }
      
      // For other errors, log and rethrow
      debugLog('BlobStorage', 'Error checking if blob exists via API', error);
      throw error;
    }
  }
  
  // Helper functions
  
  /**
   * Ensure container exists, create if it doesn't
   */
  private async ensureContainerExists(containerClient: ContainerClient): Promise<void> {
    try {
      const exists = await containerClient.exists();
      if (!exists) {
        debugLog('BlobStorage', `Creating container ${containerClient.containerName}`);
        await containerClient.create();
      }
    } catch (error) {
      debugLog('BlobStorage', 'Error ensuring container exists', error);
      throw error;
    }
  }
  
  /**
   * Generate a unique blob name with timestamp
   */
  private generateUniqueBlobName(originalName: string): string {
    const timestamp = new Date().getTime();
    const extension = originalName.includes('.')
      ? originalName.substring(originalName.lastIndexOf('.'))
      : '';
    const baseName = originalName.includes('.')
      ? originalName.substring(0, originalName.lastIndexOf('.'))
      : originalName;
    
    return `${baseName}-${timestamp}${extension}`;
  }
  
  /**
   * Convert a readable stream to a blob
   */
  private async streamToBlob(
    stream: NodeJS.ReadableStream | ReadableStream<Uint8Array>,
    contentLength: number | undefined,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const chunks: Uint8Array[] = [];
      let loadedBytes = 0;

      if ('getReader' in stream) {
        // Web ReadableStream
        const reader = (stream as ReadableStream<Uint8Array>).getReader();
        const processChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                const blob = new Blob(chunks);
                resolve(blob);
                break;
              }
              
              chunks.push(value);
              loadedBytes += value.length;
              
              if (onProgress && contentLength) {
                const percentComplete = Math.round((loadedBytes / contentLength) * 100);
                onProgress(percentComplete);
              }
            }
          } catch (err) {
            reject(err);
          }
        };
        processChunk();
      } else {
        // Node.js ReadableStream
        const nodeStream = stream as NodeJS.ReadableStream;
        nodeStream.on('data', (chunk: Uint8Array) => {
          chunks.push(chunk);
          loadedBytes += chunk.length;
          
          if (onProgress && contentLength) {
            const percentComplete = Math.round((loadedBytes / contentLength) * 100);
            onProgress(percentComplete);
          }
        });
        
        nodeStream.on('end', () => {
          const blob = new Blob(chunks);
          resolve(blob);
        });
        
        nodeStream.on('error', (err) => {
          reject(err);
        });
      }
    });
  }
  
  // Mock functions for development/testing
  
  /**
   * Mock file upload for development
   */
  private async mockUploadFile(file: File, options: UploadOptions): Promise<string> {
    // Simulate upload delay and progress
    const totalChunks = 10;
    const chunkSize = file.size / totalChunks;
    
    for (let i = 0; i < totalChunks; i++) {
      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (options.onProgress) {
        const bytesTransferred = Math.min(chunkSize * (i + 1), file.size);
        options.onProgress({
          filename: file.name,
          bytesTransferred,
          totalBytes: file.size,
          percentComplete: Math.round((bytesTransferred / file.size) * 100)
        });
      }
    }
    
    // Check for required client and workflow IDs
    if (!options.clientId || !options.workflowId) {
      throw new Error('Client ID and Workflow ID are required for file uploads');
    }
    
    // Generate a mock blob path with client/workflow organization
    const blobName = options.blobName || this.generateUniqueBlobName(file.name);
    const blobPath = `${options.containerName}/${options.clientId}/workflows/${options.workflowId}/uploads/${blobName}`;
    
    debugLog('BlobStorage', 'Mock upload completed', blobPath);
    return blobPath;
  }
  
  /**
   * Mock blob download for development
   */
  private async mockDownloadBlob(options: DownloadOptions): Promise<Blob> {
    // Create a mock blob with some text content
    const text = `This is mock content for ${options.blobName}`;
    const blob = new Blob([text], { type: 'text/plain' });
    
    // Simulate download progress
    const totalSteps = 10;
    for (let i = 0; i < totalSteps; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (options.onProgress) {
        options.onProgress(Math.round(((i + 1) / totalSteps) * 100));
      }
    }
    
    return blob;
  }
  
  /**
   * Mock listing blobs for development
   */
  private async mockListBlobs(_containerName: string, prefix?: string): Promise<string[]> {
    // Generate mock blob list
    const mockBlobs = [
      'file1.csv',
      'file2.csv',
      'results/output1.csv',
      'results/output2.csv',
      'config/settings.json'
    ];
    
    // Filter by prefix if provided
    const filteredBlobs = prefix
      ? mockBlobs.filter(blob => blob.startsWith(prefix))
      : mockBlobs;
    
    await new Promise(resolve => setTimeout(resolve, 500));
    return filteredBlobs;
  }
  
  /**
   * Mock SAS token generation
   */
  private mockGenerateSASToken(): string {
    return 'sv=2020-08-04&ss=b&srt=co&sp=rwdlacx&se=2023-12-31T23:59:59Z&st=2023-01-01T00:00:00Z&spr=https&sig=mockSignature';
  }
  
  /**
   * Mock public URL generation
   */
  private mockGetPublicUrl(containerName: string, blobName: string): string {
    const sasToken = this.mockGenerateSASToken();
    return `https://mockstorage.blob.core.windows.net/${containerName}/${blobName}?${sasToken}`;
  }
  
  /**
   * Mock blob deletion
   */
  private async mockDeleteBlob(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    debugLog('BlobStorage', 'Mock blob deleted successfully');
  }

  async saveWorkflowMetadata(clientId: string, workflowId: string, metadata: any): Promise<string> {
    if (!this.client) {
      throw new Error('BlobServiceClient not initialized');
    }
    
    const containerClient = this.client.getContainerClient(this.containerName);
    await this.ensureContainerExists(containerClient);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const blobPath = `${clientId}/workflows/${workflowId}/metadata/workflow_${timestamp}.json`;
    
    const blobClient = containerClient.getBlobClient(blobPath);
    const blockBlobClient = blobClient.getBlockBlobClient();
    await blockBlobClient.upload(JSON.stringify(metadata, null, 2), JSON.stringify(metadata).length);
    console.log(`Saved workflow metadata to ${blobPath}`);
    
    return blobPath;
  }

  async getLatestWorkflowMetadata(clientId: string, workflowId: string): Promise<any> {
    if (!this.client) {
      throw new Error('BlobServiceClient not initialized');
    }
    
    const containerClient = this.client.getContainerClient(this.containerName);
    await this.ensureContainerExists(containerClient);
    
    const prefix = `${clientId}/workflows/${workflowId}/metadata/workflow_`;
    const blobs = containerClient.listBlobsFlat({ prefix });
    
    let latestBlob: any = null;
    let latestTimestamp: string | null = null;
    
    for await (const blob of blobs) {
      const timestamp = blob.name.split('_').pop()?.replace('.json', '');
      if (timestamp && (!latestTimestamp || timestamp > latestTimestamp)) {
        latestTimestamp = timestamp;
        latestBlob = blob;
      }
    }
    
    if (latestBlob) {
      const blobClient = containerClient.getBlobClient(latestBlob.name);
      const downloadResponse = await blobClient.download();
      const content = await this.streamToText(downloadResponse.readableStreamBody);
      return JSON.parse(content);
    }
    
    console.warn(`No workflow metadata found for workflow ${workflowId}`);
    return null;
  }

  async saveModuleResults(clientId: string, workflowId: string, moduleId: string, results: any, isDraft: boolean = true): Promise<string> {
    if (!this.client) {
      throw new Error('BlobServiceClient not initialized');
    }
    
    const containerClient = this.client.getContainerClient(this.containerName);
    await this.ensureContainerExists(containerClient);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const status = isDraft ? 'draft' : 'final';
    const blobPath = `${clientId}/workflows/${workflowId}/outputs/${moduleId}/${status}_results_${timestamp}.json`;
    
    const blobClient = containerClient.getBlobClient(blobPath);
    const blockBlobClient = blobClient.getBlockBlobClient();
    await blockBlobClient.upload(JSON.stringify(results, null, 2), JSON.stringify(results).length);
    console.log(`Saved ${status} results to ${blobPath}`);
    
    return blobPath;
  }

  async getLatestModuleResults(clientId: string, workflowId: string, moduleId: string): Promise<any> {
    if (!this.client) {
      throw new Error('BlobServiceClient not initialized');
    }
    
    const containerClient = this.client.getContainerClient(this.containerName);
    await this.ensureContainerExists(containerClient);
    
    const prefix = `${clientId}/workflows/${workflowId}/outputs/${moduleId}/final_results_`;
    const blobs = containerClient.listBlobsFlat({ prefix });
    
    let latestBlob: any = null;
    let latestTimestamp: string | null = null;
    
    for await (const blob of blobs) {
      const timestamp = blob.name.split('_').pop()?.replace('.json', '');
      if (timestamp && (!latestTimestamp || timestamp > latestTimestamp)) {
        latestTimestamp = timestamp;
        latestBlob = blob;
      }
    }
    
    if (latestBlob) {
      const blobClient = containerClient.getBlobClient(latestBlob.name);
      const downloadResponse = await blobClient.download();
      const content = await this.streamToText(downloadResponse.readableStreamBody);
      return JSON.parse(content);
    }
    
    console.warn(`No final results found for module ${moduleId}`);
    return null;
  }

  private async streamToText(readableStream: NodeJS.ReadableStream | undefined): Promise<string> {
    if (!readableStream) {
      throw new Error('No readable stream available');
    }

    return new Promise((resolve, reject) => {
      const chunks: string[] = [];
      readableStream.on('data', (data) => {
        chunks.push(data.toString());
      });
      readableStream.on('end', () => {
        resolve(chunks.join(''));
      });
      readableStream.on('error', reject);
    });
  }

  /**
   * List files for a specific client and workflow
   */
  public async listFiles(options: {
    containerName: string;
    clientId: string;
    workflowId: string;
  }): Promise<any[]> {
    try {
      // In API proxy mode, use the API endpoint
      return this.listFilesViaApi(options);
    } catch (error: any) {
      console.error('Error listing files:', error);
      return [];
    }
  }

  /**
   * List files via API proxy
   */
  private async listFilesViaApi(options: {
    containerName: string;
    clientId: string;
    workflowId: string;
  }): Promise<any[]> {
    try {
      const { containerName, clientId, workflowId } = options;
      
      // Build the prefix for blob filtering
      const prefix = `${clientId}/${workflowId}`;
      
      const response = await apiClient.get('/api/v1/storage/list', {
        params: {
          container: containerName,
          prefix,
          client_id: clientId,
          workflow_id: workflowId
        }
      });
      
      if (response.data && Array.isArray(response.data.files)) {
        return response.data.files.map((file: any) => ({
          name: file.name.split('/').pop(), // Extract filename from path
          path: file.path,
          size: file.size,
          lastModified: file.lastModified,
          contentType: file.contentType
        }));
      }
      
      return [];
    } catch (error: any) {
      console.error('Error listing files via API:', error);
      return [];
    }
  }
}

// Export singleton instance
export default BlobStorageService.getInstance();
